import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  UpfrontPaymentFinancialModalWrapper,
  UpfrontPaymentButtonRow,
  UpfrontPaymentMainText,
} from "./UpfrontPayment.styles";
import {
  ProfileModalTitle,
  ProfileModalTitleWrapper,
  ProfileModalContentWrapper,
  CenteredModalContainer,
  Title,
  Subtitle,
} from "../shared/Modal/Modal.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useBillPreviewsRefresh } from "../../utils/hooks/useBillPreviewsRefresh";
import { Button } from "../shared/Button/Button";
import { SavedCardType } from "../BabyPeek/Services/BabyPeekService";
import {
  Bill,
  getCentsToDollars,
  PostUpfrontPaymentPlanPayoff,
  UpfrontPaymentReceiptType,
} from "./Services/UpfrontPaymentService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { getCardIcon } from "../BabyPeek/BabyPeekStripe";
import { EditPaymentMethodModal } from "./EditPaymentMethodModal";
import { PayoffModalContentWrapper } from "./PaymentPlanPayoffModal.styles";
import {
  PaymentMethodCard,
  PaymentMethodLabel,
  PaymentMethodInfo,
  CardInfo,
  CardIconImage,
} from "../PaymentMethod/PaymentMethod.styles";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { UpfrontPaymentReceipt } from "./UpfrontPaymentReceipt";
import { StepperIndex } from "./types";

interface PaymentPlanPayoffModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (updatedBill: Bill) => void;
  onError?: () => void;
  upfrontBill: Bill;
  currentPaymentMethod?: SavedCardType;
  onPaymentMethodUpdate: (paymentMethodInfo: {
    payment_method_id: string;
    card_brand: string;
    last_four_digits: string;
  }) => void;
}

export const PaymentPlanPayoffModal: React.FC<PaymentPlanPayoffModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  upfrontBill,
  currentPaymentMethod,
  onPaymentMethodUpdate,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const refreshBillPreviews = useBillPreviewsRefresh();
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showEditPaymentMethodModal, setShowEditPaymentMethodModal] =
    useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [originalPayoffAmount, setOriginalPayoffAmount] = useState<number>(0);
  const [receiptData, setReceiptData] =
    useState<UpfrontPaymentReceiptType | null>(null);

  const payoffAmount = upfrontBill?.balance;

  const handlePayoff = async () => {
    if (!currentPaymentMethod || !billingPK || !payoffAmount) {
      setErrorMessage(t("upfrontPayment.errors.paymentMethodMissing"));
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setOriginalPayoffAmount(payoffAmount);

    try {
      TrackAnalyticsEvent(
        "PayoffPaymentPlanSubmitted",
        undefined,
        upfrontBill.req_identifier,
        {
          interaction_type: "button_click",
          button_text: `Pay ${getCentsToDollars(payoffAmount)}`,
          element_name: "payoff_payment_plan_submit_button",
          page_name: "/client/upfront-payment",
        },
      );

      const payoffResponse = await PostUpfrontPaymentPlanPayoff(
        billingPK,
        currentPaymentMethod.payment_method_id,
        adminID,
      );

      if (payoffResponse.ok) {
        const payoffData = await payoffResponse.json();
        setReceiptData(payoffData.receipt || null);
        setPaymentSubmitted(true);
        onSuccess(payoffData.bill);
        await refreshBillPreviews();
      } else {
        const errorData = await payoffResponse.json();
        setErrorMessage(
          errorData.error ||
            t("upfrontPayment.errors.paymentCouldNotBeProcessed"),
        );
        onError?.();
      }
    } catch (error) {
      setErrorMessage(t("upfrontPayment.errors.unexpected"));
      onError?.();
    } finally {
      setLoading(false);
    }
  };

  const handleEditPaymentMethod = () => {
    TrackAnalyticsEvent("interaction", undefined, upfrontBill.req_identifier, {
      interaction_type: "button_click",
      button_text: "Edit",
      element_name: "edit_payment_method_payoff_payment_plan_inline_link",
      page_name: "/client/upfront-payment",
    });
    setShowEditPaymentMethodModal(true);
  };

  const handlePaymentMethodUpdateSuccess = (paymentMethodInfo: {
    payment_method_id: string;
    card_brand: string;
    last_four_digits: string;
  }) => {
    setShowEditPaymentMethodModal(false);
    onPaymentMethodUpdate(paymentMethodInfo);
  };

  if (!payoffAmount && !paymentSubmitted) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        style={{
          pointerEvents: loading ? "none" : undefined,
        }}
      >
        <CenteredModalContainer>
          <UpfrontPaymentFinancialModalWrapper
            deviceType={deviceType}
            allowScroll
            receipt={false}
          >
            <ProfileModalTitle deviceType={deviceType}>
              <ProfileModalTitleWrapper deviceType={deviceType}>
                <div>{t("upfrontPayment.paymentPlan.payOffInFull")}</div>
                <CloseIcon onClick={onClose} />
              </ProfileModalTitleWrapper>
            </ProfileModalTitle>

            <PayoffModalContentWrapper>
              {paymentSubmitted ? (
                <UpfrontPaymentReceipt
                  modal
                  setModal={onClose}
                  isPaymentPlan={false}
                  setActiveStep={() => {}} // Not needed for modal
                  paymentAmount={originalPayoffAmount}
                  receiptData={receiptData}
                />
              ) : (
                <ProfileModalContentWrapper>
                  {errorMessage && (
                    <ErrorPopup
                      message={errorMessage}
                      hideMessage={() => setErrorMessage("")}
                    />
                  )}
                  {loading && <SpinnerLoading />}
                  <Title>
                    {t("upfrontPayment.paymentPlan.payAmountToday", {
                      amount: getCentsToDollars(payoffAmount),
                    })}
                  </Title>
                  <Subtitle>
                    {t("upfrontPayment.paymentPlan.payoffSubtitle")}
                  </Subtitle>

                  {currentPaymentMethod && (
                    <PaymentMethodCard>
                      <PaymentMethodLabel>
                        {t(
                          "upfrontPayment.editPaymentMethod.paymentMethod",
                        ).toLocaleUpperCase()}
                      </PaymentMethodLabel>
                      <PaymentMethodInfo>
                        <CardInfo>
                          <CardIconImage
                            src={getCardIcon(currentPaymentMethod.card_brand)}
                            alt={currentPaymentMethod.card_brand}
                          />
                          {GetTypographyContent({
                            content: `${currentPaymentMethod.card_brand.toUpperCase()} •••• ${currentPaymentMethod.last_four_digits}`,
                            size: "medium",
                            textColor: "black",
                          })}
                        </CardInfo>
                        {GetTypographyContent({
                          content: t("upfrontPayment.actions.edit"),
                          size: "large",
                          textColor: "redMedium",
                          onClick: handleEditPaymentMethod,
                          className: "edit-button",
                          isCaps: false,
                        })}
                      </PaymentMethodInfo>
                    </PaymentMethodCard>
                  )}

                  <UpfrontPaymentButtonRow>
                    <Button
                      label={t("upfrontPayment.actions.payAmount", {
                        amount: getCentsToDollars(payoffAmount),
                      })}
                      size="medium"
                      backgroundColor="greenDark"
                      onClick={handlePayoff}
                      disabled={loading || !currentPaymentMethod}
                    />
                  </UpfrontPaymentButtonRow>

                  <UpfrontPaymentButtonRow bottomRow>
                    <Button
                      label={t("upfrontPayment.actions.cancel")}
                      size="medium"
                      backgroundColor="greyMedium"
                      onClick={onClose}
                      reverse
                      disabled={loading}
                    />
                  </UpfrontPaymentButtonRow>
                </ProfileModalContentWrapper>
              )}
            </PayoffModalContentWrapper>
          </UpfrontPaymentFinancialModalWrapper>
        </CenteredModalContainer>
      </Modal>

      <EditPaymentMethodModal
        open={showEditPaymentMethodModal}
        onClose={() => setShowEditPaymentMethodModal(false)}
        onSuccess={handlePaymentMethodUpdateSuccess}
        reqIdentifier={upfrontBill.req_identifier}
      />
    </>
  );
};
